import axios from "axios";

export const openRazorpay = (orderOrSubscriptionId, amount, currency, userDetails, onPaymentComplete) => {
  const isSubscription = !!userDetails.subscriptionId;
  // Set when a payment attempt really failed (bank declined etc.). A close without it is an
  // incomplete checkout, which callers can handle differently from a failed payment.
  let hadFailedAttempt = false;

  // Tell the backend the checkout didn't complete, so it can email the donor and schedule
  // reminders. Called on payment.failed AND when the donor closes / cancels the checkout
  // (Razorpay fires no payment.failed for a cancel, so those donors used to get no email).
  // Only once per checkout; the backend also de-duplicates.
  let failureReported = false;
  const reportFailure = (orderId, paymentId, reason) => {
    if (failureReported || !orderId) return;
    failureReported = true;
    const baseURL = import.meta.env.VITE_API_BASE_URL;
    const notifyUrl = baseURL.endsWith('/api')
      ? `${baseURL}/payment/notify-failed`
      : `${baseURL}/api/payment/notify-failed`;
    axios.post(notifyUrl, {
      orderId,
      paymentId,
      reason,
      ...(isSubscription && { subscriptionId: orderOrSubscriptionId }),
    }).catch(() => {});
  };

  const options = {
    key: import.meta.env.VITE_RAZORPAY_KEY_ID,
    amount: amount * 100, // Razorpay expects amount in paise
    currency,
    name: "True Hope Foundation",
    description: "Donation",
    prefill: {
      name: userDetails.fullName || userDetails.name,
      email: userDetails.email,
      contact: userDetails.phoneNumber || userDetails.phone
    },
    notes: {
      donationType: userDetails.donationType || "payonce",
      campaignId: userDetails.campaignId || "",
      campaignName: userDetails.campaignName || "",
      postalCode: userDetails.postalCode || "",
      vendorId: userDetails.vendorId || ""
    },
    modal: {
      ondismiss: function () {
        sessionStorage.removeItem("paymentInProgress");
        // Closed without paying: our own order / subscription id identifies the checkout.
        reportFailure(orderOrSubscriptionId, null, "dismissed");
        onPaymentComplete(false, {
          status: 'Cancelled',
          error: 'User closed the payment window',
          orderId: orderOrSubscriptionId, // shown as the attempt reference on the failure page
          hadFailedAttempt,
        });
      }
    },
    userId: userDetails.userId || null,
    handler: async function (response) {
      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' });
      const formattedTime = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      const formattedDateTime = `${formattedDate} | ${formattedTime}`;

      const finalPayload = {
        razorpay_order_id: response.razorpay_order_id || "0",
        razorpay_subscription_id: response.razorpay_subscription_id || "0",
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        userDetails,
        orderId: orderOrSubscriptionId,
        amount,
        currency,
        tipAmount: userDetails.tipAmount || 0, // Include tip amount in verify payload
        anonymousCheck: userDetails.anonymousCheck || 0,
        vendorId: userDetails.vendorId || null,
        paymentDateTime: formattedDateTime,
        formattedDate,
        formattedTime,
        paymentMethod: response.method || 'online'
      };

      try {
        const baseURL = import.meta.env.VITE_API_BASE_URL;
        // Construct the verify endpoint URL
        const verifyUrl = baseURL.endsWith('/api') 
          ? `${baseURL}/payment/verify` 
          : `${baseURL}/api/payment/verify`;
        
        await axios.post(verifyUrl, {
          ...finalPayload,
          status: "success"
        });
        cleanupPayment();
        onPaymentComplete(true, finalPayload);
      } catch (err) {
        console.error("Payment verification failed:", err);
        onPaymentComplete(false, finalPayload);
      }
    }
  };

  // Conditionally attach Razorpay payment type
  if (isSubscription) {
    options.subscription_id = orderOrSubscriptionId;
    delete options.amount; // Subscriptions don't need amount field
  } else {
    options.order_id = orderOrSubscriptionId;
    options.amount = amount * 100;
  }

  const rzp = new window.Razorpay(options);

  rzp.on("payment.failed", function (response) {
    console.error("Razorpay payment failed:", response.error);
    hadFailedAttempt = true;

    // Notify backend so the cron can send the retry email.
    // Fire-and-forget — do NOT call onPaymentComplete here because the modal
    // stays open for retry. Navigation happens in ondismiss when they close.
    const orderId = response.error?.metadata?.order_id || orderOrSubscriptionId;
    const paymentId = response.error?.metadata?.payment_id;
    reportFailure(orderId, paymentId, "payment_failed");
  });

  rzp.open();
};

function cleanupPayment() {
  sessionStorage.removeItem("paymentInProgress");
  sessionStorage.removeItem("orderId");
}

